/**
 * BFF Apps sidebar.
 *
 * Flat item list broken up by fixed uppercase section headings — not the expandable dropdown
 * groups `UTSideBar` renders, which is why this is local to BFF Apps rather than the shared
 * component. Nothing in `@group-link-one/gl-ui` is modified or wrapped.
 *
 * Visibility is entirely the caller's business: this renders exactly the entries it is handed.
 * {@link filterNavEntries} is what drops flag-disabled items and the section headings left
 * empty behind them.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import type { JSX, ReactNode } from 'react';
import { Badge } from '../Badge/Badge';

/** An uppercase heading that groups the items below it. */
export interface NavSection {
  kind: 'section';
  label: string;
}

/** A navigable row. */
export interface NavItem {
  kind: 'item';
  id: string;
  label: string;
  icon: IGLIcon['variant'];
  /** Route this row navigates to. */
  path: string;
  /** Count shown on the right; omitted or 0 renders nothing. */
  badge?: number;
  /** Feature-flag gate. `false` removes the row entirely — never renders it disabled. */
  isToAppear: boolean;
}

export type NavEntry = NavSection | NavItem;

/**
 * Drops flag-disabled items, then drops section headings that have no visible item left under
 * them — a heading with nothing beneath it is noise, and the handoff requires a group to appear
 * only when at least one child does.
 */
export function filterNavEntries(entries: NavEntry[]): NavEntry[] {
  const withoutHiddenItems = entries.filter(entry => entry.kind === 'section' || entry.isToAppear);

  return withoutHiddenItems.filter((entry, index) => {
    if (entry.kind !== 'section') return true;
    const next = withoutHiddenItems[index + 1];
    return next !== undefined && next.kind === 'item';
  });
}

/**
 * Onde o login deve cair: a primeira linha navegável que esta pessoa enxerga.
 *
 * Fica aqui, ao lado do filtro que monta a sidebar, porque é a mesma pergunta feita duas vezes —
 * "o que esta pessoa pode abrir?". Respondida em dois lugares, a tela de destino e o menu
 * discordam, e o sintoma é o pior possível: entrar e ver a área de conteúdo em branco, sem erro,
 * porque a rota existe e a pessoa não tem a página.
 *
 * Devolve `undefined` quando não há linha nenhuma — quem chama decide o que fazer com isso.
 */
export function firstNavPath(entries: NavEntry[]): string | undefined {
  return entries.find(entry => entry.kind === 'item')?.path;
}

const navStyle = css`
  width: var(--layout-sidebar-width);
  min-width: var(--layout-sidebar-width);
  height: 100vh;
  position: sticky;
  top: 0;
  background: var(--color-surface);
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
`;

const headerStyle = css`
  padding: 20px 18px;
  display: flex;
  align-items: center;
`;

const listStyle = css`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }
`;

const sectionStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
  padding: 14px 10px 6px;
`;

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  height: 42px;
  border-radius: var(--radius-m);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  width: 100%;
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-hover);
  }
`;

const rowActiveStyle = css`
  background: var(--color-primary-subtle);
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);

  &:hover {
    background: var(--color-primary-subtle);
  }
`;

const labelStyle = css`
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const footerStyle = css`
  border-top: 1px solid var(--color-border);
  padding: 12px;
`;

export interface SidebarNavProps {
  entries: NavEntry[];
  /** `id` of the active item. */
  activeId?: string;
  onSelect: (item: NavItem) => void;
  /** Brand block at the top. */
  header?: ReactNode;
  /** Sign-out block at the bottom. */
  footer?: ReactNode;
}

export const SidebarNav = ({
  entries,
  activeId,
  onSelect,
  header,
  footer,
}: SidebarNavProps): JSX.Element => (
  <nav className={navStyle}>
    {header ? <div className={headerStyle}>{header}</div> : null}

    <div className={listStyle}>
      {entries.map(entry => {
        if (entry.kind === 'section') {
          return (
            <div key={`section-${entry.label}`} className={sectionStyle}>
              {entry.label}
            </div>
          );
        }

        const isActive = entry.id === activeId;

        return (
          <button
            key={entry.id}
            id={`sidebar-item-${entry.id}`}
            type='button'
            className={`${rowStyle} ${isActive ? rowActiveStyle : ''}`}
            onClick={() => {
              onSelect(entry);
            }}
          >
            <GLIcon
              variant={entry.icon}
              size={20}
              fill={isActive ? 'var(--color-primary)' : 'var(--color-text-muted)'}
            />
            <span className={labelStyle}>{entry.label}</span>
            {entry.badge ? (
              <Badge tone={isActive ? 'primary' : 'neutral'}>{entry.badge}</Badge>
            ) : null}
          </button>
        );
      })}
    </div>

    {footer ? <div className={footerStyle}>{footer}</div> : null}
  </nav>
);
